import React, { useCallback, useEffect, useRef, useState } from "react";
import { UserInfo, ProjectDetail } from "./model/backendTypes";
import { listProjects, openProject } from "./api/ProjectClient";

// Extrahiert den Hue-Wert aus dem rules_xml eines Projekts (z.B. <settings hue="220">)
function extractHue(rulesXml?: string | null): number {
  if (!rulesXml) return 220;
  const match = rulesXml.match(/<settings[^>]*\shue="(\d+(\.\d+)?)"/);
  return match ? parseFloat(match[1]) : 220;
}

// Hilfsfunktion zur Formatierung des Erstellungsdatums
function formatDate(ts?: string | null): string {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
}

// =============================================================================
// ProjectOpenDropdown Komponente
// =============================================================================
// Diese Komponente stellt einen Button ("open") zur Verfügung, der beim
// Anklicken ein modern gestaltetes Dropdown-Menü öffnet. In diesem Menü
// werden alle verfügbaren Projekte des Benutzers aufgelistet. Bei Auswahl
// eines Projekts wird dieses über den onProjectChange-Callback geladen.
// =============================================================================

type Props = {
  user: UserInfo | null;
  onProjectChange?: (proj: ProjectDetail | null) => void;
  currentProjectId?: number;
};

export default function ProjectOpenDropdown({ user, onProjectChange, currentProjectId }: Props) {
  // Referenzen für DOM-Elemente zur Berechnung der Positionierung
  const rootRef = useRef<HTMLDivElement | null>(null);

  // UI- und Daten-Status
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<ProjectDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // ---------------------------------------------------------------------------
  // Lädt die Projekte des aktuellen Benutzers asynchron vom Backend.
  // ---------------------------------------------------------------------------
  const loadProjects = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const p = await listProjects(user.id);
      setProjects(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ---------------------------------------------------------------------------
  // Event-Listener zum Schließen des Dropdowns bei:
  // - Klicks außerhalb des Komponenten-Containers (rootRef)
  // - Drücken der Escape-Taste
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (!rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // ---------------------------------------------------------------------------
  // Steuert das Verhalten, wenn das Dropdown geöffnet oder geschlossen wird:
  // Stößt den Ladevorgang (loadProjects) an, wenn es geöffnet wird.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) {
      setSearchQuery(""); // Suche zurücksetzen, wenn geschlossen wird
    } else {
      loadProjects();
    }
  }, [open, loadProjects]);

  // ---------------------------------------------------------------------------
  // Handler für die Auswahl eines bestimmten Projekts aus der Liste.
  // Kontaktiert das Backend, lädt das Projekt und gibt es an die App-Ebene weiter.
  // ---------------------------------------------------------------------------
  const handleSelectProject = async (projectId: number) => {
    if (!user || !onProjectChange) return;
    try {
      const proj = await openProject(user.id, projectId);
      onProjectChange(proj);
      setOpen(false); // Menü schließen nach erfolgreicher Auswahl
    } catch (e) {
      console.error("Project open failed:", e);
      alert("The project could not be opened.");
    }
  };

  // Filtern der Projekte nach dem Suchbegriff
  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.id.toString().includes(searchQuery)
  );

  return (
    // Der root-Container dient als Ankerpunkt für die DOM-Click-Erkennung.
    <div className="profile-menu" ref={rootRef}>
      
      {/* Trigger-Button für das Dropdown-Menü */}
      <button
        type="button"
        className="header-ghost-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        disabled={!user}
      >
        open
      </button>

      {/* Rendering des eigentlichen Dropdown-Inhalts, falls "open" === true */}
      {open && (
        <div 
          className="profile-menu__dropdown profile-menu__dropdown--compact profile-menu__dropdown--left" 
          role="menu"
        >
          <div className="profile-menu__section-title" style={{ marginTop: 0 }}>Open Project</div>
          
          {/* Status-Anzeigen (Laden, Fehler, Leer) */}
          {loading && <div className="profile-menu__status-text">Loading projects...</div>}
          
          {error && <div className="billing-panel__error" style={{ margin: "8px 0" }}>{error}</div>}

          {!loading && !error && projects.length === 0 && (
             <div className="profile-menu__status-text">No projects available.</div>
          )}

          {/* Sucheingabe und Projektliste */}
          {!loading && !error && projects.length > 0 && (
            <>
              {projects.length > 5 && (
                <div style={{ padding: "0 0 8px 0" }}>
                  <input
                    type="text"
                    className="profile-menu__search-input"
                    placeholder="Suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    spellCheck="false"
                  />
                </div>
              )}

              <div 
                className="profile-menu__submenu" 
                style={{ 
                  maxHeight: "185px", // Ca. Platz für 3 Projekte, danach scrollt es
                  overflowY: "auto", 
                  paddingRight: "4px" 
                }}
              >
                {filteredProjects.length === 0 ? (
                  <div className="profile-menu__status-text" style={{ padding: "4px 8px" }}>Nothing found.</div>
                ) : (
                  filteredProjects.map(p => {
                    const isActive = p.id === currentProjectId;
                    const hue = extractHue(p.rules_xml);
                    
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`profile-menu__project-btn ${isActive ? "profile-menu__project-btn--active" : ""}`}
                        onClick={() => handleSelectProject(p.id)}
                        disabled={isActive}
                        title={isActive ? "Derzeit geöffnetes Projekt" : `Projekt wechseln zu '${p.name}'`}
                        style={{
                          backgroundColor: isActive
                            ? `oklch(0.75 0.15 ${hue} / 0.25)`
                            : `oklch(0.85 0.07 ${hue} / 0.15)`,
                          borderColor: isActive
                            ? `oklch(0.55 0.18 ${hue} / 0.85)`
                            : `oklch(0.65 0.12 ${hue} / 0.25)`,
                          color: isActive
                            ? undefined
                            : `oklch(0.50 0.10 ${hue})`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" }}>
                          {/* Datei-Icon mit projektspezifischer Hue-Farbe */}
                          <div
                            className="profile-menu__project-icon"
                            style={{
                              backgroundColor: `oklch(0.75 0.12 ${hue} / 0.4)`,
                              color: `oklch(0.40 0.18 ${hue})`,
                              borderColor: `oklch(0.60 0.14 ${hue} / 0.5)`,
                            }}
                          >
                            📁
                          </div>
                          
                          {/* Projekt-Name & Datum */}
                          <div className="profile-menu__project-content">
                            <span className="profile-menu__project-name">
                              {p.name}
                            </span>
                            {p.created_at && (
                              <span className="profile-menu__project-date">
                                Erstellt: {formatDate(p.created_at)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ID als minimalistisches Badge */}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span className="profile-menu__project-id">
                            ID {p.id}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
